import {createSelector, createEntityAdapter, EntityState} from '@reduxjs/toolkit'
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import {IPaginationMetadata, ISearchEntities} from '../shared/interfaces'
import {RootState} from '../../../data/redux/Store'
import {AUTH_TOKEN_KEY, env} from '../../../data/constants'
import {IFinancierCall} from '../financiers/interfaces'

const callsAdapter = createEntityAdapter<IFinancierCall>({
  selectId: (call) => call.id,
})

const callsInitialState = callsAdapter.getInitialState()

const baseQuery = fetchBaseQuery({
  baseUrl: env.Financiers,
  validateStatus: (response, result) => {
    if (result && result.isError) return false
    if (response.status >= 200 && response.status <= 300) return true
    if (response.status >= 400 && response.status <= 500) return false
    return false
  },
  prepareHeaders: (headers, {getState}) => {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEY)
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }
    return headers
  },
})

const customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions)
  if (result.error) {
    const status = result.error.status

    throw new Error(`${status}`)
  }

  return result
}

export const CallsApiSlice = createApi({
  reducerPath: 'callsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['Calls'],
  endpoints: (builder) => ({
    getCalls: builder.query<
      {entities: EntityState<IFinancierCall>; paginationMetadata: IPaginationMetadata},
      {searchParams: ISearchEntities}
    >({
      query: ({searchParams}) => {
        const params = new URLSearchParams()

        if (searchParams.page) params.append('page', searchParams.page.toString())
        if (searchParams.pageSize) params.append('page_size', searchParams.pageSize.toString())
        if (searchParams.startDate) params.append('start_date', searchParams.startDate)
        if (searchParams.endDate) params.append('end_date', searchParams.endDate)
        if (searchParams.countries) {
          searchParams.countries.forEach((country) =>
            params.append('country', country.charAt(0).toUpperCase() + country.slice(1))
          )
        }
        if (searchParams.sectors) {
          searchParams.sectors.forEach((sector) => params.append('sectors', sector))
        }
        if (searchParams.financierId) {
          params.append('financier_id', searchParams.financierId)
        }
        if (searchParams.isPublished !== undefined) {
          params.append('isPublished', "yes")
        }

        return {
          url: '/calls/',
          params: params,
        }
      },
      transformResponse: (res: any) => {
        const callsData: IFinancierCall[] = res.result.calls.map((call: any) => ({
          id: call.id,
          financierId: call.financier_id,
          financierName: call.financier,
          url: call.url,
          name: call.name,
          description: call.description,
          applicationCount: call.application_count,
          department: call.department,
          category: call.category,
          logo: call.logo,
          requirements: call.requirements,
          deadline: call.deadline,
          numberOfRecruitments: call.number_of_recruitments,
          sectors: call.sectors,
          country: call.country,
          region: call.region,
          dateUploaded: call.date_uploaded,
          callAmount: call.call_amount,
          currency: call.currency,
        }))
        const entities = callsAdapter.setAll(callsInitialState, callsData)

        return {
          entities,
          paginationMetadata: {
            totalCount: res.result.total_count,
            pageSize: res.result.page_size,
            hasNextPage: res.result.next ? true : false,
            hasPreviousPage: res.result.previous ? true : false,
          },
        }
      },
      providesTags: (result: {entities: EntityState<IFinancierCall>} | undefined) => {
        if (result?.entities.ids) {
          return [
            {type: 'Calls', id: 'LIST'},
            ...result.entities.ids.map((id) => ({type: 'Calls' as const, id})),
          ]
        } else {
          return [{type: 'Calls', id: 'LIST'}]
        }
      },
    }),

    sendWhatsAppMessage: builder.mutation<
      {success: boolean; message: string},
      {
        sectors: string[]
        minRevenue: string
        maxRevenue: string
        callId: string
        callName: string
        phoneNumbers?: string[]
      }
    >({
      query: (payload) => ({
        url: '/calls/send-whatsapp',
        method: 'POST',
        body: payload,
      }),
    }),

    sendLeadsWhatsAppMessage: builder.mutation<
      {success: boolean; message: string},
      {
        callId: string
        callName: string
        phoneNumbers?: string[]
      }
    >({
      query: (payload) => ({
        url: '/calls/send-whatsapp',
        method: 'PUT',
        body: payload,
      }),
    }),

    sendEmailMessage: builder.mutation<
      {success: boolean; message: string},
      {sectors: string[]; minRevenue: string; maxRevenue: string; callId: string; callName: string}
    >({
      query: (payload) => ({
        url: '/calls/send-email',
        method: 'POST',
        body: {
          ...payload,
          actionType: 'message', // ✅ here you set it by default
        },
      }),
    }),

    sendFinancierInvite: builder.mutation<{success: boolean; message: string}, FormData>({
      query: (payload) => ({
        url: '/calls/send-email',
        method: 'POST',
        body: payload,
      }),
    }),
  }),
})

export const {
  useGetCallsQuery,
  useSendWhatsAppMessageMutation,
  useSendEmailMessageMutation,
  useSendLeadsWhatsAppMessageMutation,
  useSendFinancierInviteMutation,
} = CallsApiSlice

export const selectFinancierCallsResult = (state: RootState, searchParams: ISearchEntities = {}) =>
  CallsApiSlice.endpoints.getCalls.select({searchParams})(state)

export const selectCallsData = (searchParams: ISearchEntities = {}) =>
  createSelector(
    (state: RootState) => selectFinancierCallsResult(state, searchParams),
    (callsResult) => callsResult?.data?.entities ?? callsInitialState
  )

export const createCallsSelectors = (searchParams: ISearchEntities = {}) => {
  const selectData = selectCallsData(searchParams)
  return callsAdapter.getSelectors((state: RootState) => selectData(state))
}
